import React, { useEffect, useRef, useState } from 'react';
import { Platform } from '../../lib/platform';
import { openNativeRecoveryTarget } from '../../lib/nativeNavigation';

interface NativeRecoveryActionProps {
  platform: Platform;
  /** Target dispatched on activation. Never rendered into the DOM or an href. */
  url: string;
  /** Primary styling; quiet renders as the secondary bordered button. */
  quiet?: boolean;
  /** Optional guidance rendered under the button. */
  note?: React.ReactNode;
  /** Copy for the unhandled-scheme fallback. */
  fallbackNote?: React.ReactNode;
}

// If a custom-scheme navigation reaches an installed app, the page loses
// focus (visibilitychange/pagehide/blur). When none of those fire, the
// browser silently ignored the scheme — that is the "unresponsive button"
// case, and it must surface a browser-usable fallback instead of dead air.
const OPEN_FALLBACK_MS = 2500;

const NativeRecoveryAction: React.FC<NativeRecoveryActionProps> = ({
  platform,
  url,
  quiet = false,
  note,
  fallbackNote,
}) => {
  const [openFailed, setOpenFailed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFallbackTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  useEffect(() => {
    // Page hidden or blurred after dispatch → treat as a successful handoff.
    const settled = () => clearFallbackTimer();
    const onVisibility = () => { if (document.hidden) settled(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', settled);
    window.addEventListener('blur', settled);
    return () => {
      clearFallbackTimer();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', settled);
      window.removeEventListener('blur', settled);
    };
  }, []);

  const handleOpen = () => {
    setOpenFailed(false);
    openNativeRecoveryTarget(url);
    clearFallbackTimer();
    timer.current = setTimeout(() => {
      if (!document.hidden) setOpenFailed(true);
    }, OPEN_FALLBACK_MS);
  };

  return (
    <div style={{ marginBottom: '1rem' }}>
      <button
        type="button"
        className={`utility-button${quiet ? ' utility-button--quiet' : ''}`}
        onClick={handleOpen}
      >
        Open RidePulse
      </button>
      {note && (
        <p className="utility-muted" style={{ marginTop: '0.75rem' }}>{note}</p>
      )}
      {openFailed && (
        <div className="utility-message utility-message--error" role="alert" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
          {fallbackNote ??
            (platform === 'android'
              ? 'RidePulse did not open. If the updated app is installed, open it directly — otherwise finish the reset in this browser or send a new link.'
              : 'RidePulse did not open. If the app is installed, open it directly — otherwise finish the reset in this browser or send a new link.')}
        </div>
      )}
    </div>
  );
};

export default NativeRecoveryAction;
